import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  ChevronDown,
  LogOut,
  Menu,
  ShoppingBag,
  User,
} from "lucide-react";

const getDisplayName = user => {
  const fullName = [user?.firstname, user?.lastname].filter(Boolean).join(" ");
  return fullName || user?.name || user?.email || "Admin";
};

const pageMeta = [
  {
    matcher: path => path === "/admin",
    title: "Dashboard",
    subtitle: "Monitor ecommerce performance, orders and catalog activity.",
  },
  {
    matcher: path => path.startsWith("/admin/products"),
    title: "Products",
    subtitle: "Manage products, stock, pricing and availability.",
  },
  {
    matcher: path => path.startsWith("/admin/orders"),
    title: "Order Management",
    subtitle: "Track orders, payment, fulfillment and customer activity.",
  },
  {
    matcher: path => path.startsWith("/admin/categories"),
    title: "Categories",
    subtitle: "Organize product categories and storefront navigation.",
  },
  {
    matcher: path => path.startsWith("/admin/coupons"),
    title: "Coupons",
    subtitle: "Create and manage discounts and promotions.",
  },
  {
    matcher: path => path.startsWith("/admin/banners"),
    title: "Banners",
    subtitle: "Manage homepage and promotional banners.",
  },
  {
    matcher: path => path.startsWith("/admin/about"),
    title: "About Us",
    subtitle: "Manage brand content and storefront story pages.",
  },
  {
    matcher: path => path.startsWith("/admin/rkicha-home"),
    title: "RKICHA Home",
    subtitle: "Manage homepage sections and merchandising content.",
  },
  {
    matcher: path => path.startsWith("/admin/messages"),
    title: "Messages",
    subtitle: "Review customer messages and support requests.",
  },
  {
    matcher: path => path.startsWith("/admin/settings"),
    title: "Settings",
    subtitle: "Manage admin configuration and storefront behavior.",
  },
  {
    matcher: path => path.startsWith("/admin/admins"),
    title: "Admins",
    subtitle: "Manage administrator accounts and permissions.",
  },
  {
    matcher: path => path.startsWith("/admin/users"),
    title: "Users",
    subtitle: "Manage customer accounts and activity.",
  },
  {
    matcher: path => path.startsWith("/admin/payment-notices"),
    title: "Payment Notices",
    subtitle: "View and manage payment-related notifications.",
  },
];

const AdminTopbar = ({ user, onOpenSidebar, onLogout }) => {
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const location = useLocation();
  const currentPage = pageMeta.find(item => item.matcher(location.pathname)) || pageMeta[0];
  const displayName = getDisplayName(user);
  const role = user?.role
    ? `${user.role.charAt(0).toUpperCase()}${user.role.slice(1)}`
    : "Administrator";

  return (
    <header className="sticky top-0 z-50 border-b border-[#EADFD7] bg-white/95 shadow-sm backdrop-blur">
      <div className="flex min-h-20 items-center gap-3 px-4 sm:px-6 lg:px-8">
        <button
          type="button"
          onClick={onOpenSidebar}
          className="flex h-11 w-11 items-center justify-center rounded-2xl border border-[#EADFD7] bg-[#FBF8F5] text-stone-700 hover:bg-[#F3E8DF] focus-visible:ring-2 focus-visible:ring-[#8B5E46] lg:hidden"
          aria-label="Open navigation"
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="min-w-0 flex-1">
          <h1 className="truncate text-xl font-black tracking-tight text-stone-950 sm:text-2xl">
            {currentPage.title}
          </h1>
          <p className="truncate text-sm font-medium text-[#7C6F66]">
            {currentPage.subtitle}
          </p>
        </div>

        <div className="flex items-center gap-2">

          <div className="relative">
            <button
              type="button"
              onClick={() => setProfileMenuOpen(open => !open)}
              onBlur={() => setTimeout(() => setProfileMenuOpen(false), 160)}
              className="flex items-center gap-3 rounded-2xl border border-[#EADFD7] bg-white px-2 py-2 text-left hover:bg-[#FBF8F5] focus-visible:ring-2 focus-visible:ring-[#8B5E46] sm:pl-2 sm:pr-3"
              aria-haspopup="menu"
              aria-expanded={profileMenuOpen}
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#8B5E46] text-sm font-black text-white shadow-sm">
                {displayName.charAt(0).toUpperCase()}
              </span>
              <span className="hidden min-w-0 lg:block">
                <span className="block truncate text-sm font-black text-stone-950">
                  {displayName}
                </span>
                <span className="block truncate text-xs font-semibold text-[#7C6F66]">
                  {role}
                </span>
              </span>
              <ChevronDown className="hidden h-4 w-4 text-[#9A8C83] sm:block" aria-hidden="true" />
            </button>

            <div
              className={`absolute right-0 top-full mt-3 w-60 rounded-2xl border border-[#EADFD7] bg-white p-2 shadow-xl transition-all ${
                profileMenuOpen
                  ? "visible translate-y-0 opacity-100"
                  : "invisible -translate-y-2 opacity-0"
              }`}
              role="menu"
            >
              <Link
                to="/profile?tab=profile"
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-stone-700 hover:bg-[#FBF8F5]"
                role="menuitem"
              >
                <User className="h-4 w-4 text-[#8B5E46]" aria-hidden="true" />
                My Profile
              </Link>
              <Link
                to="/"
                className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-bold text-stone-700 hover:bg-[#FBF8F5]"
                role="menuitem"
              >
                <ShoppingBag className="h-4 w-4 text-[#8B5E46]" aria-hidden="true" />
                Go to store
              </Link>
              <button
                type="button"
                onClick={onLogout}
                className="mt-2 flex w-full items-center gap-3 border-t border-[#F1E8E0] px-3 py-2.5 text-left text-sm font-bold text-red-600 hover:bg-red-50"
                role="menuitem"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
};

export default AdminTopbar;
